/**
 * Who they are — from their own onboarding form, or from us on their behalf.
 *
 * Mostly typed by the customer. When the person who started the form has gone,
 * or they give the answers on a call or by email, a platform administrator
 * enters them with "Edit answers" — and every answer of ours says so beside it
 * (StaffMark), with how we got it. Nothing we typed is shown as the customer's.
 */
import { useState, type ReactNode } from 'react';
import { formatDayUS } from '../../../../shared/dates';
import page from '../../../../shared/Page.module.css';
import { ProvenanceNote } from '../ProvenanceNote';
import { StageNeeds } from '../StageNeeds';
import { SendIntakeForm } from '../SendIntakeForm';
import { DomainsPanel } from '../DomainsPanel';
import { IntakeEditor, type KnownPerson } from '../IntakeEditor';
import { usePrepare } from '../jump';
import { primaryFor } from '../people';
import { StaffMark, staffWords } from '../StaffMark';
import { organizationTypeLabel } from '../../../intake/organizationTypes';
import type { Onboarding, Stage } from '../types';
import { GroupCard } from '../../../../shared/GroupCard';

const day = formatDayUS;

export function OrganizationStage({
  data, stage, onViewIntake,
}: {
  data: Onboarding; stage: Stage; onViewIntake: () => void;
}) {
  const i = data.intake;
  const absent = <span className={page.absent}>Not given</span>;

  const [editing, setEditing] = useState(false);
  usePrepare((what) => what === 'editIntake', () => setEditing(true));
  // Who is already recorded, so a person section can be copied rather than retyped.
  const known: Record<string, KnownPerson> = {};
  for (const role of ['champion', 'signatory', 'billing'] as const) {
    const p = primaryFor(data.people, role);
    if (p) known[role] = { name: p.name, email: p.email, title: p.title, phone: p.phone, from: 'Contacts' };
  }
  const sa = data.sysAdmins[0];
  if (sa) known.systemAdmin = { name: sa.displayName ?? sa.email, email: sa.email, from: 'Administrators' };
  const staff = i?.staffFields ?? {};
  const hasAnswers = !!i && (!!i.submittedAt || !!i.legalName || !!i.address
    || i.locationCount !== null || !!i.organizationType || !!i.targetGoLiveDate);
  const fact = (label: string, key: string, shown: ReactNode) => (
    <div><dt className={page.factTerm}>{label}</dt>
      <dd className={page.factValue}>{shown}<StaffMark field={staff[key]} /></dd></div>
  );

  return (
    <>
      {!i?.submittedAt && (
        <div className={`${page.notice} ${page.noticeInfo}`}>
          <strong>{hasAnswers ? 'Not returned yet.' : 'Nothing back from the customer yet.'}</strong>{' '}
          {data.intakeLinkSent
            ? 'Their onboarding form has been sent and not returned. Everything below waits on it, '
              + 'unless they give you the answers another way — then use Edit answers.'
            : 'Send them their onboarding form below, or enter their answers yourself with Edit answers.'}
        </div>
      )}

      {/*
        * OURS AND THEIRS IN ONE GROUP. The organization id is ours and always
        * present — support needs it — so the group shows even before the
        * customer has answered anything.
        */}
      <GroupCard title="Their details">
        {hasAnswers && i ? (
          <>
            <dl className={page.facts}>
              {fact('Legal name', 'organization.legalName', i.legalName || absent)}
              {fact('Kind of organization', 'organization.organizationType', organizationTypeLabel(i.organizationType) ?? absent)}
              {fact('Address', 'organization.address', i.address || absent)}
              {fact('Locations', 'organization.locationCount', i.locationCount ?? absent)}
              {fact('Target go-live', 'organization.targetGoLiveDate', day(i.targetGoLiveDate) ?? absent)}
            </dl>
            {/* Once, under the group. Who returned it: them, or us for them. */}
            {i.submittedAt && (i.returnedBy
              ? <div className={page.itemMeta}>Form returned for them · {staffWords(i.returnedBy)}</div>
              : <ProvenanceNote who={i.submitter.name} when={i.submittedAt} />)}
          </>
        ) : (
          <p className={page.absent}>Nothing recorded yet.</p>
        )}
        <p className={page.itemMeta} style={{ marginTop: 'var(--sp-3)' }}>
          Organization ID:{' '}
          <span style={{ fontFamily: 'var(--font-mono)' }}>{data.orgId}</span>
        </p>
        {!editing && (
          <div className={page.actions} style={{ marginTop: 'var(--sp-3)' }}>
            <button type="button" className={page.btn} onClick={() => setEditing(true)}>Edit answers</button>
            {i && (
              <button type="button" className={page.btn} onClick={onViewIntake}>
                View everything {i.submittedAt ? 'they submitted' : 'so far'}
              </button>
            )}
          </div>
        )}
      </GroupCard>

      {editing && <IntakeEditor orgId={data.orgId} onDone={() => setEditing(false)} known={known} />}

      {!i?.submittedAt && !editing && <SendIntakeForm data={data} />}

      <DomainsPanel orgId={data.orgId} domains={data.domains} />

      <StageNeeds requirements={stage.requirements} />
    </>
  );
}
